/**
 * CustomPassageModal.jsx — "Bring your own passage" gateway.
 *
 * Feb 13 2026 user follow-up ("wire potential"). The user pastes any
 * text (or picks an ebook from the library), the backend generates 3
 * comprehension MCQs + 3 vocab items via our AI provider, then
 * this modal offers a drill picker so the reader can practise on
 * their own text instantly.
 *
 * The generated passage is stashed in sessionStorage under
 * `learn_custom_passage`; every drill page checks that key on mount
 * and, when present, runs against the custom passage instead of the
 * bundled bank.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wand2, Loader2, X, BookOpen, FileText, Sparkles, ChevronDown, Search,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DRILLS = [
  { key: 'chunking',    label: 'Chunking',   route: '/learn/chunking',    accent: 'text-cyan-300 border-cyan-500/40' },
  { key: 'pacer',       label: 'Pacer',      route: '/learn/pacer',       accent: 'text-fuchsia-300 border-fuchsia-500/40' },
  { key: 'timed-quiz',  label: 'Timed + Q',  route: '/learn/timed-quiz',  accent: 'text-amber-300 border-amber-500/40' },
  { key: 'regression',  label: 'Regression', route: '/learn/regression',  accent: 'text-emerald-300 border-emerald-500/40' },
  { key: 'preview',     label: 'Preview',    route: '/learn/preview-read',accent: 'text-indigo-300 border-indigo-500/40' },
  { key: 'scan',        label: 'Skim/Scan',  route: '/learn/scan',        accent: 'text-cyan-300 border-cyan-500/40' },
  { key: 'fixations',   label: 'Fixations',  route: '/learn/fixations',   accent: 'text-rose-300 border-rose-500/40' },
  { key: 'vocab',       label: 'Vocab Deck', route: '/learn/vocab',       accent: 'text-amber-300 border-amber-500/40' },
];

const CustomPassageModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState('paste'); // paste | library
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const [selectedBook, setSelectedBook] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(null); // { passage_id, text, questions, vocab, ... }

  // Reset every time the modal opens
  useEffect(() => {
    if (!open) return;
    setText(''); setTitle(''); setSelectedBook(''); setError(''); setGenerated(null);
    setMode('paste');
  }, [open]);

  // Lazy-load the library ebook list only when the user switches to
  // "library" mode — keeps the initial modal open snappy.
  useEffect(() => {
    if (!open || mode !== 'library' || libraryBooks.length) return;
    (async () => {
      try {
        // Feb 14 2026 fix: the previous /api/content/ebook path 404s.
        // /api/library is the real catalog endpoint. Books store the
        // full body in `text_content`; `content` is a short blurb we
        // only fall back to.
        const r = await authFetch(`${BACKEND_URL}/api/library`);
        if (r.ok) {
          const d = await r.json();
          const rawBooks = Array.isArray(d.books)
            ? d.books
            : (d.content || d.items || []);
          const list = rawBooks.map((b) => {
            const body = b.text_content || b.content || b.description || b.text || '';
            return {
              id: b.book_id || b.content_id || b.id,
              title: b.title || 'Untitled',
              author: b.author || 'Unknown',
              content: body,
            };
          }).filter((b) => (b.content || '').split(/\s+/).filter(Boolean).length >= 50);
          setLibraryBooks(list);
        }
      } catch { /* non-fatal */ }
      finally { setLibraryLoaded(true); }
    })();
  }, [open, mode, libraryBooks.length]);

  const wordCount = (text || '').split(/\s+/).filter(Boolean).length;
  const canGenerate = wordCount >= 50 && !busy;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setBusy(true); setError('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/learn/custom-passage/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.slice(0, 8000),
          title: title || undefined,
          source: mode,
        }),
      });
      if (r.status === 402) {
        let msg = 'Free trial ended. Upgrade to Premium to keep training.';
        try { const d = await r.json(); if (d?.detail?.message) msg = d.detail.message; } catch { /* no-op */ }
        setError(msg);
        return;
      }
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setGenerated(d);
      // Stash for the drill pages to pick up on mount.
      try {
        sessionStorage.setItem('learn_custom_passage', JSON.stringify(d));
      } catch { /* quota / private mode */ }
    } catch (e) {
      setError(e?.message || 'Could not generate');
    } finally {
      setBusy(false);
    }
  };

  const pickBook = (id) => {
    setSelectedBook(id);
    const b = libraryBooks.find((x) => x.id === id);
    if (b) {
      setText(b.content);
      setTitle(`${b.title} — ${b.author}`);
    }
  };

  const gotoDrill = (route) => {
    onClose && onClose();
    navigate(route);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="learn-custom-passage-modal"
    >
      <div className="w-full max-w-xl bg-[#12131A] border border-cyan-500/30 rounded-2xl p-5 sm:p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="eyebrow text-cyan-300 mb-1 flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> Bring your own passage
            </p>
            <h3 className="font-display text-2xl font-black text-white tracking-tight">
              Practise on any text
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white text-2xl leading-none w-8 h-8 -mr-2"
            aria-label="Close"
            data-testid="learn-custom-passage-close"
          >
            ×
          </button>
        </div>

        {!generated && (
          <>
            {/* Mode selector — paste vs. library */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setMode('paste')}
                className={`px-3 py-2 rounded-lg border text-xs font-black tracking-tight transition-colors inline-flex items-center justify-center gap-2 ${
                  mode === 'paste'
                    ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200'
                    : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.06]'
                }`}
                data-testid="learn-custom-passage-mode-paste"
              >
                <FileText className="w-3.5 h-3.5" /> Paste text
              </button>
              <button
                onClick={() => setMode('library')}
                className={`px-3 py-2 rounded-lg border text-xs font-black tracking-tight transition-colors inline-flex items-center justify-center gap-2 ${
                  mode === 'library'
                    ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200'
                    : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.06]'
                }`}
                data-testid="learn-custom-passage-mode-library"
              >
                <BookOpen className="w-3.5 h-3.5" /> From library
              </button>
            </div>

            {mode === 'library' && (
              <div className="mb-3">
                <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 font-black mb-1.5">
                  Ebook
                </label>
                <div className="relative">
                  <select
                    value={selectedBook}
                    onChange={(e) => pickBook(e.target.value)}
                    className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400/60"
                    data-testid="learn-custom-passage-book-select"
                  >
                    <option value="">
                      {libraryBooks === null || libraryBooks.length === 0
                        ? 'Loading…'
                        : '— pick a book —'}
                    </option>
                    {libraryBooks.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.title} · {b.author}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                </div>
                {libraryLoaded && libraryBooks.length === 0 && (
                  <p className="mt-2 text-[11px] text-amber-300/80">
                    No library books with ≥50 words of readable text yet. Paste text instead.
                  </p>
                )}
              </div>
            )}

            <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 font-black mb-1.5">
              Title <span className="text-white/25">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What is this passage?"
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/60 mb-3"
              data-testid="learn-custom-passage-title"
            />

            <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 font-black mb-1.5">
              Passage text <span className="text-white/25">({wordCount} / 500 words · min 50)</span>
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder="Paste any text — an article, chapter opening, essay, transcript, etc. Anything 50–500 words works. Server auto-generates 3 comprehension MCQs + 3 vocab cards for you."
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/60 leading-relaxed"
              data-testid="learn-custom-passage-text"
            />

            {error && (
              <p className="mt-3 text-xs text-rose-300 flex items-center gap-1.5">
                {error}
              </p>
            )}

            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 px-4 py-3 text-sm font-black tracking-tight transition-colors"
              data-testid="learn-custom-passage-generate"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {busy ? 'Generating drill set…' : 'Generate drill set'}
            </button>
            <p className="text-[11px] text-white/40 mt-2 text-center">
              Questions are generated server-side and never leave the server
            </p>
          </>
        )}

        {generated && (
          <div data-testid="learn-custom-passage-picker">
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 px-3 py-2.5 mb-4 flex items-center gap-2 text-emerald-200 text-sm">
              <Sparkles className="w-4 h-4" />
              Drill set ready — {generated.word_count} words · 3 questions · 3 vocab cards.
            </div>
            <p className="eyebrow text-cyan-300 mb-2">Pick a drill</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {DRILLS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => gotoDrill(d.route)}
                  className={`rounded-lg border ${d.accent} bg-white/[0.03] hover:bg-white/[0.08] px-2 py-3 text-xs font-black tracking-tight`}
                  data-testid={`learn-custom-passage-drill-${d.key}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-white/40 mt-2 text-center">
              Your custom passage stays selected until you generate a new one or clear it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPassageModal;
